import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, UrlTree } from "@angular/router";
import { Observable, from, of } from "rxjs";
import { catchError, finalize, first, switchMap, tap } from "rxjs/operators";
import { LoadingStateHandlerService } from "@shared/services/loading-state/loading-state-handler.service";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ModalService } from "@core/services/modal-service/modal.service";
import { TermsConditionsModalComponent } from "@shared/components/terms-conditions-modal/terms-conditions-modal.component";
import type { TermsConditionsModalResult } from "@core/models/terms-conditions-modal-result.model";
import { ActionOptionsEnum } from "@core/enums/action-options.enum";
import { TermsService } from "@shared/services/api-service/terms.service";
import { DetailedCourseStateService } from "@shared/services/detailed-course-state/detailed-course-state.service";
import { CourseDetail, LessonProgress } from "@core/models/course-detail.model";
import type { TermsConditions } from "@core/models/terms-conditions.model";
import { TermsConditionsPositionEnum } from "@core/enums/terms-conditions-position.enum";

export interface RunCourseTermsGuardConfig {
  courseId: number;
  termsPosition: TermsConditionsPositionEnum;
  route: ActivatedRouteSnapshot | null;
}

interface ResolveTermsAfterLoadParams extends RunCourseTermsGuardConfig {
  courseDetail: CourseDetail;
}

const MAX_PERCENTAGE_PROGRESS: number = 100;

export const COURSE_TERMS_GUARD_LOADING_CONTEXT: string = "CourseTermsGuard";
export const COURSE_TERMS_GUARD_LOADING_KEYS: { readonly courseLoad: string } = {
  courseLoad: "courseLoad",
};

@Injectable({
  providedIn: "root",
})
export class ManagementCourseTermsConditionsService {
  private readonly modalService: ModalService = inject(ModalService);
  private readonly termsService: TermsService = inject(TermsService);
  private readonly detailedCourseStateService: DetailedCourseStateService = inject(DetailedCourseStateService);
  private readonly router: Router = inject(Router);
  private readonly loadingState: LoadingStateHandlerService = inject(LoadingStateHandlerService);

  runCourseTermsGuard$(config: RunCourseTermsGuardConfig): Observable<boolean | UrlTree> {
    const { courseId, termsPosition }: RunCourseTermsGuardConfig = config;
    const forceReloadCourse: boolean = termsPosition === TermsConditionsPositionEnum.END;

    const needsNetworkFetch: boolean =
      forceReloadCourse || !this.detailedCourseStateService.isCourseCached(courseId);

    return this.detailedCourseStateService.loadCourseData$({ courseId, force: forceReloadCourse }).pipe(
      tap(() => {
        if (!needsNetworkFetch) return;

        this.loadingState.loadDataState(COURSE_TERMS_GUARD_LOADING_CONTEXT, {
          [COURSE_TERMS_GUARD_LOADING_KEYS.courseLoad]: true,
        });
        this.loadingState.setDataLoading(
          COURSE_TERMS_GUARD_LOADING_CONTEXT,
          COURSE_TERMS_GUARD_LOADING_KEYS.courseLoad,
        );
      }),
      first(),
      switchMap((detail: CourseDetail) => this.resolveTermsAfterLoad({ ...config, courseDetail: detail })),
      catchError(() => this.onCourseLoadFailed(courseId)),
      finalize(() => {
        if (!needsNetworkFetch) return;

        this.loadingState.setDataLoaded(
          COURSE_TERMS_GUARD_LOADING_CONTEXT,
          COURSE_TERMS_GUARD_LOADING_KEYS.courseLoad,
        );
      }),
    );
  }

  private findPendingTerm(detail: CourseDetail, position: TermsConditionsPositionEnum): TermsConditions | undefined {
    return (detail.termsConditions ?? []).find(
      (term: TermsConditions) => term.position === position && !term.hasUserConfirm,
    );
  }

  private openTermsAcceptanceModal(term: TermsConditions): NgbModalRef {
    return this.modalService.open<TermsConditionsModalComponent>({
      component: TermsConditionsModalComponent,
      inputs: {
        term,
        confirmPersistence: () => this.termsService.acceptTerm$(term.id),
      },
      ngbOptions: { size: "xl" },
    });
  }

  private observeModalResolvedResult(modalRef: NgbModalRef): Observable<TermsConditionsModalResult> {
    return from(modalRef.result as Promise<TermsConditionsModalResult>).pipe(
      finalize(() => this.modalService.dismiss("navigationAway")),
    );
  }

  private resolveTermsAfterLoad(params: ResolveTermsAfterLoadParams): Observable<boolean | UrlTree> {
    const { courseDetail, courseId, route, termsPosition }: ResolveTermsAfterLoadParams = params;

    if(termsPosition === TermsConditionsPositionEnum.END && !this.areAllLessonsCompleted(courseDetail)) return of(true);

    const pendingTerm: TermsConditions | undefined = this.findPendingTerm(courseDetail, termsPosition);
    if(pendingTerm === undefined) return of(true);

    const modalRef: NgbModalRef = this.openTermsAcceptanceModal(pendingTerm);

    const declineContext: RunCourseTermsGuardConfig = { courseId, route, termsPosition };

    return this.observeModalResolvedResult(modalRef).pipe(
      switchMap((result: TermsConditionsModalResult) => {
        if (result.action !== ActionOptionsEnum.CONFIRM) return this.afterTermsDeclined(declineContext);

        this.detailedCourseStateService.markCourseTermsConfirmedLocally(courseId, [termsPosition]);
        return of(true);
      }),
      catchError(() => this.afterTermsDeclined(declineContext)),
    );
  }

  private afterTermsDeclined(ctx: RunCourseTermsGuardConfig): Observable<boolean | UrlTree> {
    const { courseId, route, termsPosition }: RunCourseTermsGuardConfig = ctx;

    if (termsPosition === TermsConditionsPositionEnum.START) return this.redirectToCourseDetails$(courseId);

    return route?.paramMap.has("lessonId") === true ? this.redirectToCourseDetails$(courseId) : of(true);
  }

  private onCourseLoadFailed(courseId: number): Observable<boolean | UrlTree> {
    if (!Number.isFinite(courseId) || courseId <= 0) return of(this.router.createUrlTree(["/catalogue"]));

    return this.redirectToCourseDetails$(courseId);
  }

  private redirectToCourseDetails$(courseId: number): Observable<boolean | UrlTree> {
    return of(this.router.createUrlTree(["/course-details", courseId]));
  }

  private areAllLessonsCompleted(detail: CourseDetail): boolean {
    const lessons: LessonProgress[] = detail.lessons ?? [];
    if (lessons.length === 0) return false;

    return lessons.every((lesson: LessonProgress) => lesson.progress === MAX_PERCENTAGE_PROGRESS);
  }
}
